import React, { useMemo } from "react";
import { Row, Col, OverlayTrigger, Tooltip, Button } from "react-bootstrap";

import FormSelect from "app/Components/_form/FormSelect/FormSelect";
import FormDateTimePicker from "app/Components/_form/FormDatePicker/FormDateTimePicker";
import {
  TRAINEE_TYPE_OPTION,
  TRAINING_TYPE_OPTION,
  trainerTypeList,
  trainerTypeObj,
} from "../../CONSTANTS";
import {
  useArea,
  useCategory,
  useContractorPerson,
  useContractorPersonDropdown,
  useEmployee,
  useEmployeeDropdown,
} from "app/_helpers/hooks";
import FormInput from "app/Components/_form/FormInput/FormInput";
import DatePicker from "app/Components/DateTimeCustom/CustomDateTime";
import FormTextArea from "app/Components/_form/FormTextArea/FormTextArea";
import SingleFileUpload from "app/views/custom/components/FileUpload/SingleFileUpload";
import SimpleSelect from "app/Components/SearchableSelect/SimpleSelect";
import SelectSearch from "app/Components/SearchableSelect/Select";
import { FiMinusCircle, FiPlusCircle } from "react-icons/fi";
import { TRAINING_SESSION_OPTION } from "../Assign";

function TrainingFormUI({
  form = {},
  onChange,
  error = {},
  onChangeDate,
  deleteDocumentFile,
  onFileChange,
  staffList,
  addRow,
  deleteRow,
  onChangeStaff,
  staffError,
  otherStaffList,
  addOtherStaff,
  deleteOtherStaff,
  onChangeOtherStaff,
  otherStaffError,
}) {
  // ********** Date Validation ************ //
  const [, employeesOption] = useEmployeeDropdown();
  const [category, categoryOption] = useCategory("filter[source]=training");

  const [, areaOption] = useArea();

  const [, contractorPersonsOption] = useContractorPersonDropdown();

  const subcategoryOption = useMemo(
    () =>
      (category.find((x) => x.id === form.trainingCategory)?.subCategory || []).map(
        (d) => ({ label: d.name, id: d.id })
      ),
    [category, form.trainingCategory]
  );

  const traineeOption = useMemo(() => {
    switch (form?.traineeType) {
      case "employee":
        return employeesOption?.filter((obj) => obj?.employmentStatus === "active");
      case "contractorPerson":
        return contractorPersonsOption?.filter(
          (obj) => obj?.employmentStatus === "active"
        );

      default:
        return [];
    }
  }, [contractorPersonsOption, employeesOption, form?.traineeType]);

  return (
    <Row>
      <Col md={4}>
        <FormSelect
          name="trainingType"
          value={form.trainingType}
          onChange={onChange}
          options={TRAINING_TYPE_OPTION}
          label={"Training Type"}
          error={error?.trainingType}
          disabled
        ></FormSelect>
      </Col>

      <Col md={4}>
        <FormInput
          name="centerName"
          value={form?.centerName}
          onChange={onChange}
          label={"Name of the Centre"}
          error={error?.centerName}
        ></FormInput>
      </Col>

      <Col md={4}>
        <FormInput
          name="centerCode"
          value={form?.centerCode}
          onChange={onChange}
          label={"Code no. of the Training Centre"}
          error={error?.centerCode}
        ></FormInput>
      </Col>
      <Col md={4}>
        <FormSelect
          name="areaId"
          value={form.areaId}
          onChange={onChange}
          options={areaOption}
          label={"Site Specific Area"}
          error={error?.areaId}
        ></FormSelect>
      </Col>

      <Col lg={4}>
        <FormInput
          label={"Training ID (To be provided by VTO)"}
          name="trainingId"
          value={form?.trainingId}
          onChange={onChange}
          error={error?.trainingId}
        />
      </Col>
      <Col lg={4}>
        <FormInput
          label={"Batch ID (To be provided by VTO)"}
          name="batchId"
          value={form?.batchId}
          onChange={onChange}
          error={error?.batchId}
        />
      </Col>
      <Col lg={4}>
        <FormInput
          label={"Training Hours"}
          name="trainingHours"
          type="number"
          value={form?.trainingHours}
          onChange={onChange}
          error={error?.trainingHours}
        />
      </Col>
      <Col md={4}>
        <FormInput
          name="duration"
          value={form?.duration}
          onChange={onChange}
          label={"Duration (Days)"}
          error={error?.duration}
        ></FormInput>
      </Col>

      <Col md={4}>
        <FormDateTimePicker
          value={form.trainingDate}
          name="trainingDate"
          onChangeDate={onChange}
          timeFormat={"HH:mm:ss"}
          dateFormat="YYYY-MM-DD"
          label={"Date of commencement of training"}
          error={error?.trainingDate}
        />
      </Col>

      <Col lg={4}>
        <FormDateTimePicker
          value={form.endDate}
          name="endDate"
          onChangeDate={onChange}
          // timeFormat={"HH:mm:ss"}
          dateFormat="YYYY-MM-DD"
          label={"End Date Of Training"}
          error={error?.endDate}
        />
      </Col>

      <Col lg={4}>
        <FormSelect
          label={"Training Category (Skill Area)"}
          name="trainingCategory"
          value={form?.trainingCategory}
          onChange={onChange}
          options={categoryOption}
          error={error?.trainingCategory}
          blankSelect={"Select"}
          menuPortalTarget={null}
        ></FormSelect>
      </Col>
      <Col lg={4}>
        <FormSelect
          label={"Training Category (Skill Subarea)"}
          name="trainingSubCategory"
          value={form?.trainingSubCategory}
          onChange={onChange}
          options={subcategoryOption}
          error={error?.trainingSubCategory}
          blankSelect={"Select"}
          // menuPortalTarget={null}
        ></FormSelect>
      </Col>

      <Col lg={4}>
        <FormSelect
          label={"Session"}
          name="session"
          value={form?.session}
          onChange={onChange}
          options={TRAINING_SESSION_OPTION}
          error={error?.session}
          blankSelect={"Select"}
          // menuPortalTarget={null}
        ></FormSelect>
      </Col>

      <Col lg={4}>
        <FormSelect
          label={"Trainer Type"}
          name="trainerType"
          value={form?.trainerType}
          onChange={onChange}
          options={trainerTypeList}
          error={error?.trainerType}
          blankSelect={"Select"}
          // menuPortalTarget={null}
        ></FormSelect>
      </Col>

      <Col lg={4}>
        {form?.trainerType !== trainerTypeObj.external ? (
          <FormSelect
            label={"Trainer Name"}
            name="trainerId"
            value={form?.trainerId}
            onChange={onChange}
            options={employeesOption?.filter((obj) => obj?.employmentStatus === "active")}
            error={error?.trainerId}
            blankSelect={"Select"}
            // menuPortalTarget={null}
          ></FormSelect>
        ) : (
          <FormInput
            name="trainerName"
            value={form?.trainerName}
            onChange={onChange}
            label={"Trainer Name"}
            error={error?.trainerName}
          ></FormInput>
        )}
      </Col>

      {/* <Col md={4}>
        <FormSelect
          name="traineeType"
          value={form.traineeType}
          onChange={onChange}
          options={TRAINEE_TYPE_OPTION}
          label={"Trainee Type"}
          error={error?.traineeType}
        ></FormSelect>
      </Col>
      <Col md={4}>
        <FormSelect
          name="traineeId"
          value={form.traineeId}
          onChange={onChange}
          options={traineeOption}
          label={"Trainee"}
          error={error?.traineeId}
        ></FormSelect>
      </Col> */}

      {/* <Col md={4}>
        <label className="mb-1">Completion Date</label>
        <DatePicker
          name="trainingCompleteDate"
          value={form?.trainingCompleteDate}
          onChange={(moment, name) =>
            typeof moment !== "string" &&
            onChange({ target: { name, value: moment.format("YYYY-MM-DD") } })
          }
          //isValidDate={disableFutureDate}
        />
        <span className="small text-danger">{error?.trainingCompleteDate}</span>
      </Col> */}

      {/* <Col md={4}>
        <div className="form-group viewbox">
          <label>Feedback/Test</label>
          <SingleFileUpload
            clsName="form-group"
            alias={false}
            disabled={form?.eventStatus == "planned" ? true : false}
            labelName=" "
            selectedFile={form.feedbackTestFile}
            deleteDocumentFile={() => deleteDocumentFile()}
            onFileChange={onFileChange}
          />
          <span className="small text-danger">{errorForm.completionProofId}</span>
        </div>
      </Col> */}

      <Col md={4}>
        <FormInput
          name="descriptionOther"
          value={form?.descriptionOther}
          onChange={onChange}
          label={'Description of "Other" Training'}
          error={error?.descriptionOther}
        ></FormInput>
      </Col>

      <Col md={8}>
        <FormTextArea
          name="comment"
          value={form?.comment}
          onChange={onChange}
          label={"Comment"}
          error={error?.comment}
          rows={1}
        />
      </Col>

      <AddEmployeeList
        staffList={staffList}
        addRow={addRow}
        deleteRow={deleteRow}
        onChangeStaff={onChangeStaff}
        staffError={staffError}
        employeesOption={employeesOption?.filter(
          (obj) => obj?.employmentStatus === "active"
        )}
        contractorPersonsOption={contractorPersonsOption?.filter(
          (obj) => obj?.employmentStatus === "active"
        )}
      />

      <AddOtherTraineeList
        otherStaffList={otherStaffList}
        addRow={addOtherStaff}
        deleteRow={deleteOtherStaff}
        onChangeOtherStaff={onChangeOtherStaff}
        otherStaffError={otherStaffError}
      />

      {/* <Col md={4}>
        <FormSelect
          name="batchId"
          value={form.batchId}
          onChange={onChange}
          options={TRAINING_TYPE_OPTION}
          label={"Training ID/Batch ID"}
          error={error?.batchId}
          disabled
        ></FormSelect>
      </Col> */}
    </Row>
  );
}

export default TrainingFormUI;

export const AddEmployeeList = ({
  staffList,
  addRow,
  deleteRow,
  onChangeStaff,
  staffError,
  employeesOption,
  contractorPersonsOption,
}) => {
  return (
    <Col lg={12}>
      <div className="form-group viewbox">
        <h5 className="w-500 mb-1">Select Trainees</h5>
        <div className="w-100">
          <div className="table-responsive">
            <table id="user_table" className="table table-bordered mb-3">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Trainee Type</th>
                  <th scope="col">Trainee Name</th>
                  <th scope="col">Remove</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((x, index) => {
                  return x.deleted ? null : (
                    <tr key={index}>
                      <th>{index + 1}</th>
                      <td>
                        <SimpleSelect
                          name="staffType"
                          value={x.staffType}
                          onChange={(event) => onChangeStaff(event, index)}
                          className="form-control"
                          id="id"
                          label="name"
                          options={[
                            { id: "employee", name: "Employee" },
                            {
                              id: "contractorPerson",
                              name: "Contractor Person",
                            },
                          ]}
                          init="Select"
                        ></SimpleSelect>

                        <span className="small text-danger">
                          {staffError[index]?.staffType}
                        </span>
                      </td>
                      <td>
                        <FormSelect
                          name="traineeId"
                          value={+x.traineeId}
                          onChange={(event) => onChangeStaff(event, index)}
                          options={
                            !x.staffType
                              ? []
                              : x.staffType === "employee"
                              ? employeesOption
                              : contractorPersonsOption
                          }
                          // label={"Trainee"}
                          error={staffError[index]?.traineeId}
                        ></FormSelect>
                      </td>
                      <td>
                        {staffList.length !== 1 && (
                          <div className="actionIconsGroup">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id="tooltip-top">Delete</Tooltip>}
                            >
                              <span
                                onClick={() => deleteRow(index)}
                                className="pointer icons text-danger font-20"
                              >
                                <FiMinusCircle />
                              </span>
                            </OverlayTrigger>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan="10">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-top">Add Row</Tooltip>}
                    >
                      <Button className="addRowPluseIcon" onClick={addRow}>
                        <FiPlusCircle />
                      </Button>
                    </OverlayTrigger>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Col>
  );
};

export const AddOtherTraineeList = ({
  otherStaffList,
  addRow,
  deleteRow,
  onChangeOtherStaff,
  otherStaffError,
}) => {
  return (
    <Col lg={12}>
      <div className="form-group viewbox">
        <h5 className="w-500 mb-1">Trainee others than Employee/Contractor staff</h5>
        <div className="w-100">
          <div className="table-responsive">
            <table id="user_table" className="table table-bordered mb-3">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Trainee Name</th>
                  <th scope="col">Remove</th>
                </tr>
              </thead>
              <tbody>
                {otherStaffList.map((x, index) => {
                  return x.deleted ? null : (
                    <tr key={index}>
                      <th>{index + 1}</th>
                      <td>
                        <FormInput
                          name="name"
                          value={x?.name}
                          onChange={(event) => onChangeOtherStaff(event, index)}
                          error={otherStaffError[index]?.name}
                        ></FormInput>
                      </td>
                      <td>
                        <div className="actionIconsGroup">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-top">Delete</Tooltip>}
                          >
                            <span
                              onClick={() => deleteRow(index)}
                              className="pointer icons text-danger font-20"
                            >
                              <FiMinusCircle />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan="10">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-top">Add Row</Tooltip>}
                    >
                      <Button className="addRowPluseIcon" onClick={addRow}>
                        <FiPlusCircle />
                      </Button>
                    </OverlayTrigger>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Col>
  );
};
